import { PrinterOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Radio, Space } from 'antd';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import MultiTableExcel from '../../../../components/common/MultiTableExcel';
import PurchaseAndPayments from '../components/PurchaseAndPayments';
import SalesAndCollectionTables from '../components/SalesAndCollectionTables';
import {
  useTodayPurchasePaymentsQuery,
  useTodaySalesReportQuery,
} from '../endpoints/totalCollectionPayments';
import {
  ITodayPaymentReport,
  ITodayPurchaseReport,
  ITodaySalesReportCollection,
  ITodaySalesReportSales,
} from '../types/totalCollectionTypes';

type Props = {};

export default function TotalCollectionPayments({}: Props) {
  const [value, setValue] = useState<'sales&collection' | 'sales&payments'>('sales&collection');

  const { data: salesAndCollection } = useTodaySalesReportQuery();
  const { data: purchaseAndPayment } = useTodayPurchasePaymentsQuery();

  const refSales = useRef(null);
  const refPayment = useRef(null);
  const refSalesPurchase = useRef(null);
  const refSalesPayment = useRef(null);

  const cancel = (e: any) => {
    if (value === 'sales&collection') {
      //'Print Sales'
      handleSales();
    } else {
      //Print Purchase
      handleRefSalesPurchase();
    }
  };

  const confirm = (e: any) => {
    if (value === 'sales&collection') {
      //Print Collection
      handleRefPayment();
    } else {
      //Print Payments
      handleRefSalesPayment();
    }
  };

  const handleSales = useReactToPrint({
    content: () => refSales.current,
    pageStyle: () => {
      return `@page { size: a4; margin: none; } @media print { body { -webkit-print-color-adjust: exact; padding: 0px !important; } }`;
    },
  });
  const handleRefPayment = useReactToPrint({
    content: () => refPayment.current,
    pageStyle: () => {
      return `@page { size: a4; margin: none; } @media print { body { -webkit-print-color-adjust: exact; padding: 0px !important; } }`;
    },
  });
  const handleRefSalesPurchase = useReactToPrint({
    content: () => refSalesPurchase.current,
    pageStyle: () => {
      return `@page { size: a4; margin: none; } @media print { body { -webkit-print-color-adjust: exact; padding: 0px !important; } }`;
    },
  });
  const handleRefSalesPayment = useReactToPrint({
    content: () => refSalesPayment.current,
    pageStyle: () => {
      return `@page { size: a4; margin: none; } @media print { body { -webkit-print-color-adjust: exact; padding: 0px !important; } }`;
    },
  });

  return (
    <>
      <BreadCrumb
        arrOfOption={[
          'Reports',
          value === 'sales&collection' ? 'Sales & Collection' : 'Purchase & Payments',
        ]}
      />
      <Space style={{ marginBottom: '1rem' }}>
        <Popconfirm
          title={'Select Print Table'}
          icon={<PrinterOutlined />}
          description=' . '
          onConfirm={confirm}
          onCancel={cancel}
          cancelText={value === 'sales&collection' ? 'Print Sales' : 'Print Purchase'}
          okText={value === 'sales&collection' ? 'Print Collection' : 'Print Payments'}
          placement='bottom'
        >
          <Button type='primary'>
            <PrinterOutlined />
            Print
          </Button>
        </Popconfirm>
        <MultiTableExcel
          fileName={`${
            value === 'sales&collection' ? 'sales_collection_report' : 'purchase_payments_report'
          }`}
          sections={[
            ...(value === 'sales&collection'
              ? [
                  {
                    title: 'SALES REPORT',
                    headers: ['Sales Date', 'Invoice No.', 'Client', 'Sales Amount'],
                    rows:
                      salesAndCollection?.data?.sales?.map((item: ITodaySalesReportSales) => ({
                        'Sales Date': item.sales_date
                          ? dayjs(item.sales_date).format('DD-MM-YYYY')
                          : '',
                        'Invoice No.': item.invoice_no || 'N/A',
                        Client: item.client_name || 'N/A',
                        'Sales Amount': Number(item.sales_price ?? 0),
                      })) || [],
                  },
                  {
                    title: 'COLLECTION REPORT',
                    headers: ['Collection Date', 'Voucher No.', 'Client', 'Collected Amount'],
                    rows:
                      salesAndCollection?.data?.collection?.map(
                        (item: ITodaySalesReportCollection) => ({
                          'Collection Date': item.receipt_payment_date
                            ? dayjs(item.receipt_payment_date).format('DD-MM-YYYY')
                            : '',
                          'Voucher No.': item.receipt_vouchar_no || 'N/A',
                          Client: item.client_name || 'N/A',
                          'Collected Amount': Number(item.receipt_total_amount ?? 0),
                        })
                      ) || [],
                  },
                ]
              : [
                  {
                    title: 'PURCHASE REPORT',
                    headers: ['Purchase Date', 'Invoice No.', 'Vendor', 'Purchase Amount'],
                    rows:
                      purchaseAndPayment?.data?.purchase?.map((item: ITodayPurchaseReport) => ({
                        'Purchase Date': item.invoice_sales_date
                          ? dayjs(item.invoice_sales_date).format('DD-MM-YYYY')
                          : '',
                        'Invoice No.': item.invoice_no || 'N/A',
                        Vendor: item.vendor_name || 'N/A',
                        'Purchase Amount': Number(item.cost_price ?? 0),
                      })) || [],
                  },
                  {
                    title: 'PAYMENT REPORT',
                    headers: [
                      'Payment Date',
                      'Voucher No.',
                      'Invoice No.',
                      'Receipt No',
                      'Vendor',
                      'Payment Amount',
                    ],
                    rows:
                      purchaseAndPayment?.data?.payments?.map((item: ITodayPaymentReport) => ({
                        'Payment Date': item.payment_date
                          ? dayjs(item.payment_date).format('DD-MM-YYYY')
                          : '',
                        'Voucher No.': item.vouchar_no || 'N/A',
                        'Invoice No.': item.invoice_no || 'N/A',
                        'Receipt No.': item.vpay_receipt_no || 'N/A',
                        Vendor: item.vendor_name || 'N/A',
                        'Payment Amount': Number(item.payment_amount ?? 0),
                      })) || [],
                  },
                ]),
          ]}
        />
      </Space>

      <Space style={{ padding: '0px 10px' }}>
        <Radio.Group value={value} onChange={(e) => setValue(e.target.value)}>
          <Radio.Button value='sales&collection'>Sales & Collection</Radio.Button>
          <Radio.Button value='sales&payments'>Purchase & Payments</Radio.Button>
        </Radio.Group>
      </Space>

      <div>
        {value === 'sales&collection' ? (
          <SalesAndCollectionTables value={value} refPayment={refPayment} refSales={refSales} />
        ) : (
          <PurchaseAndPayments
            value={value}
            refSalesPayment={refSalesPayment}
            refSalesPurchase={refSalesPurchase}
          />
        )}
      </div>
    </>
  );
}
